import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, ArrowRight, Eye, EyeOff } from "lucide-react";

const PasswordStrength = ({ password }) => {
  if (!password) return null;
  const checks = [
    { label: "8 caractères minimum", ok: password.length >= 8 },
    { label: "Au moins une lettre", ok: /[a-zA-Z]/.test(password) },
    { label: "Au moins un chiffre", ok: /\d/.test(password) },
  ];
  const score = checks.filter((c) => c.ok).length;
  const color = score === 3 ? "bg-green-500" : score === 2 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < score ? color : "bg-white/10"}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {checks.map((c) => (
          <span key={c.label} className={`text-[10px] ${c.ok ? "text-green-400/70" : "text-white/25"}`}>
            {c.ok ? "✓" : "·"} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const Login = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setPassword("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(username, password);
      } else {
        await register(username, password);
      }
      navigate("/dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (mode === "login") {
        setError(msg || "Identifiants incorrects.");
      } else {
        setError(msg || "Erreur lors de la création du compte.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_center,_rgba(120,80,200,0.08)_0%,_transparent_70%)]" />
      </div>

      <div className="relative w-full max-w-[380px]">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2.5 mb-10">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center">
            <LayoutGrid size={18} className="text-black" />
          </div>
          <span className="text-white font-semibold text-xl tracking-tight">AllSave</span>
        </div>

        {/* Mode toggle */}
        <div className="flex items-center bg-white/[0.04] rounded-xl p-1 border border-white/[0.06] mb-8">
          {["login", "register"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => switchMode(m)}
              className={`flex-1 py-2 rounded-lg text-[13px] font-medium transition-all ${
                mode === m ? "bg-white text-black" : "text-white/40 hover:text-white/70"
              }`}
            >
              {m === "login" ? "Connexion" : "Créer un compte"}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[11px] text-[#666] uppercase tracking-widest font-medium mb-2 block">
              Nom d'utilisateur
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={mode === "login" ? "demo" : "votre_pseudo"}
              className="w-full bg-[#0c0c0e] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.2] transition-colors"
              required
              minLength={3}
              data-testid="username-input"
            />
          </div>

          <div>
            <label className="text-[11px] text-[#666] uppercase tracking-widest font-medium mb-2 block">
              Mot de passe
            </label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "login" ? "demo" : "8 caractères minimum"}
                className="w-full bg-[#0c0c0e] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#333] focus:outline-none focus:border-white/[0.2] transition-colors pr-11"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#444] hover:text-white/60 transition-colors"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {mode === "register" && <PasswordStrength password={password} />}
          </div>

          {error && (
            <div className="px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-[12px]">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black font-medium text-sm rounded-xl py-3 flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] disabled:opacity-50"
            data-testid="login-btn"
          >
            {loading
              ? mode === "login" ? "Connexion…" : "Création…"
              : mode === "login" ? "Se connecter" : "Créer le compte"}
            {!loading && <ArrowRight size={15} />}
          </button>
        </form>

        {mode === "login" && (
          <p className="text-center text-[#333] text-xs mt-6">
            Compte démo : <span className="text-[#666]">demo</span> / <span className="text-[#666]">demo</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
